import { Button, Group, Popover, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { formatDateTimeMinus } from 'src/variable/constant'

type BannerSchedulePickerProps = {
	startAt: Date | null
	endAt: Date | null
	onChange: (value: { startAt: Date | null, endAt: Date | null }) => void
	startError?: React.ReactNode
	endError?: React.ReactNode
}

type BannerDateTimePickerProps = {
	label: string
	value: Date | null
	onChange: (value: Date | null) => void
	error?: React.ReactNode
}

const hours = Array.from({ length: 24 }, (_, value) => String(value).padStart(2, '0'))
const minutes = Array.from({ length: 60 }, (_, value) => String(value).padStart(2, '0'))

const formatValue = (value: Date | null) => value ? dayjs(value).format(formatDateTimeMinus) : ''

export const applyDatePart = (datePart: Date, timeSource: Date | null) => {
	const result = new Date(datePart)
	result.setHours(timeSource?.getHours() ?? 0, timeSource?.getMinutes() ?? 0, 0, 0)
	return result
}

export const applyTimePart = (value: Date, hour: string | null, minute: string | null) => {
	const result = new Date(value)
	result.setHours(Number(hour ?? value.getHours()), Number(minute ?? value.getMinutes()), 0, 0)
	return result
}

export const getTodayDateTime = (value: Date | null, now = new Date()) =>
	applyDatePart(dayjs(now).startOf('minute').toDate(), value || now)

function BannerSchedulePicker({ startAt, endAt, onChange, startError, endError }: BannerSchedulePickerProps) {
	return <Group grow align="flex-start">
		<BannerDateTimePicker
			label="Start time"
			value={startAt}
			error={startError}
			onChange={(value) => onChange({ startAt: value, endAt })}
		/>
		<BannerDateTimePicker
			label="End time"
			value={endAt}
			error={endError}
			onChange={(value) => onChange({ startAt, endAt: value })}
		/>
	</Group>
}

function BannerDateTimePicker({ label, value, onChange, error }: BannerDateTimePickerProps) {
	const [opened, setOpened] = useState(false)
	const [draftValue, setDraftValue] = useState<Date | null>(value)

	useEffect(() => {
		if (!opened) setDraftValue(value)
	}, [opened, value])

	const updateTime = (hour?: string | null, minute?: string | null) => {
		if (!draftValue) return
		setDraftValue(applyTimePart(
			draftValue,
			hour ?? String(draftValue.getHours()),
			minute ?? String(draftValue.getMinutes()),
		))
	}

	return <Popover opened={opened} onChange={setOpened} position="bottom-start" width={440} shadow="md" withinPortal>
		<Popover.Target>
			<div onClick={() => setOpened(true)}>
				<TextInput
					withAsterisk
					label={label}
					placeholder={formatDateTimeMinus}
					value={formatValue(value)}
					readOnly
					rightSection={<IconCalendar size={16} />}
					error={error}
				/>
			</div>
		</Popover.Target>
		<Popover.Dropdown>
			<Stack spacing="sm">
				<Text size="sm" weight={500}>Select {label.toLowerCase()}</Text>
				<Group align="flex-start" spacing="xl" noWrap>
					<DatePicker
						value={draftValue}
						onChange={(date) => setDraftValue(date ? applyDatePart(date, draftValue) : null)}
					/>
					<Stack spacing="xs" sx={{ flex: 1, minWidth: 170 }}>
						<SimpleGrid cols={2} spacing="xs">
							<Select
								label="Hour"
								placeholder="HH"
								data={hours}
								value={draftValue ? String(draftValue.getHours()).padStart(2, '0') : null}
								disabled={!draftValue}
								onChange={(hour) => updateTime(hour)}
							/>
							<Select
								label="Minute"
								placeholder="mm"
								data={minutes}
								value={draftValue ? String(draftValue.getMinutes()).padStart(2, '0') : null}
								disabled={!draftValue}
								onChange={(minute) => updateTime(undefined, minute)}
							/>
						</SimpleGrid>
					</Stack>
				</Group>
				<Group position="apart">
					<Group spacing="xs">
						<Button compact variant="subtle" onClick={() => {
							onChange(null)
							setOpened(false)
						}}>Clear</Button>
						<Button compact variant="subtle" onClick={() => setDraftValue(getTodayDateTime(draftValue))}>Today</Button>
					</Group>
					<Group spacing="xs">
						<Button compact variant="default" onClick={() => {
							setDraftValue(value)
							setOpened(false)
						}}>Cancel</Button>
						<Button compact disabled={!draftValue} onClick={() => {
							onChange(draftValue)
							setOpened(false)
						}}>Apply</Button>
					</Group>
				</Group>
			</Stack>
		</Popover.Dropdown>
	</Popover>
}

export default BannerSchedulePicker
